/**
 * Timesheet — CATS-build reference data primed from the read-only ZUI_M12_*
 * OData services (Phase 3, Option A).
 *
 * `timesheetDataService.bootstrapReferenceData()` calls these in the S/4 build
 * (`VITE_TS_BACKEND=cats`) so the SYNCHRONOUS getters (`getEmployee(s)`,
 * `getChargeCode(s)`) keep returning from the in-memory `refData` cache with no
 * network hop, exactly as they do in Supabase mode. Everything here is
 * best-effort: any failure leaves the seeds in place (the shell still renders).
 *
 * Charge objects come from the same released services the live app already
 * consumes (`ZUI_M12_WBS_STRUCT_O4`, `ZUI_M12_PRDORD_O4`); employees come from
 * `ZUI_M12_PERSONNEL_O4`. They are reached SAME-ORIGIN under the BSP (and via the
 * `/sap` dev proxy locally), not through the live app's `/api/s4` Vercel proxy.
 *
 * See: docs/architecture/handoffs/PHASE_3_HANDOFF.md §6.
 */
import type { TsChargeCode, TsEmployee, TsWorkCategory } from '../types/timesheet.types'
import {
  sapChargeObjectToChargeCode,
  sapChargeObjectId,
  normalizeWbsKey,
  type SapChargeObject,
  type SapObjectType,
} from '../lib/sapChargeObjects'
import { odataList, odataListAt } from './catsOData'
import { chargeCodeIdForRow, splitChargeCodeId, type TimeSheetRow } from './catsMapper'
import { normalizePernr } from '../lib/catsPayableRows'

// Sibling read-only services (same-origin, /srvd/). These are the exact paths
// the live app's SAP_OBJECT_CONNECTORS use under /api/s4, rebased to same-origin
// for the S/4-hosted build.
const WBS_URL = '/sap/opu/odata4/sap/zui_m12_wbs_struct_o4/srvd/sap/z_m12_wbs_struct_sd/0001/WbsStructure'
const PRDORD_URL = '/sap/opu/odata4/sap/zui_m12_prdord_o4/srvd/sap/z_m12_prdord_sd/0001/ProductionOrder'
const PERSONNEL_URL = '/sap/opu/odata4/sap/zui_m12_personnel_o4/srvd/sap/zui_m12_personnel/0001/Personnel'

const PAGE_TOP = 5000

export interface ChargeObjectCodeSets {
  /** Active + selectable for new entries. */
  active: TsChargeCode[]
  /** All (incl. inactive) — resolver so historical entries render their label. */
  all: TsChargeCode[]
}

// Index signatures: the whole OData row is stored as the charge object's loose
// `attributes` bag, so the row type must be assignable to Record<string, unknown>.
interface WbsRow {
  WbsElement?: string
  WbsElementExternal?: string
  Description?: string
  CompanyCode?: string
  [k: string]: unknown
}

interface PrdOrdRow {
  ProductionOrder?: string
  ProductionOrderExternal?: string
  Description?: string
  [k: string]: unknown
}

interface PersonnelRow {
  PersonnelNumber: string
  FullName?: string
  FirstName?: string
  LastName?: string
  HomeCostCenter?: string
  CompanyCode?: string
  /** IT0001-PERSG / PERSK. Drive pay class on the S/4 build (ts_pay_class_rules). */
  EmployeeGroup?: string
  EmployeeSubgroup?: string
  /** IT0001-WERKS / BTRTL, the SITE. With group/subgroup these scope pay class
   *  and Time Evaluation rules. */
  PersonnelArea?: string
  PersonnelSubarea?: string
  EmploymentStatus?: string
  /** IT0315 (Time Sheet Defaults) master activity type (LSTAR). */
  DefaultActivityType?: string
  /** Activity type text (CSLT) for DefaultActivityType. */
  DefaultActivityTypeName?: string
  /** IT0315 timesheet cost center (KOSTL). */
  TimesheetCostCenter?: string
  /** Cost center name (CSKT) for TimesheetCostCenter. */
  TimesheetCostCenterName?: string
  /** Cost center name (CSKT) for HomeCostCenter. */
  HomeCostCenterName?: string
  /** 'manufacturing' when the IT0315 activity type is listed in ZTM12_TS_MFGAT
   *  (the ManufacturingActivityType entity), otherwise 'office'. Drives the
   *  kiosk / manufacturing day and week surfaces. */
  WorkCategory?: string
}

/** Absent on a system whose personnel view predates the field: leave the
 *  category unset there rather than calling everyone an office worker. */
function rowWorkCategory(r: PersonnelRow): TsWorkCategory | undefined {
  const c = (r.WorkCategory || '').trim()
  return c === 'manufacturing' || c === 'office' ? c : undefined
}

function wbsRowToChargeObject(r: WbsRow): SapChargeObject | null {
  const key = normalizeWbsKey((r.WbsElementExternal || r.WbsElement || '').trim())
  if (!key) return null
  return toSapChargeObject('wbs_element', key, r.Description ?? null, r.CompanyCode ?? null, r)
}

function prdOrdRowToChargeObject(r: PrdOrdRow): SapChargeObject | null {
  const key = normalizeWbsKey((r.ProductionOrderExternal || r.ProductionOrder || '').trim())
  if (!key) return null
  return toSapChargeObject('production_order', key, r.Description ?? null, null, r)
}

function toSapChargeObject(
  type: SapObjectType,
  key: string,
  description: string | null,
  companyCode: string | null,
  attributes: Record<string, unknown>,
): SapChargeObject {
  return {
    id: sapChargeObjectId(type, key),
    orgId: 'sap',
    sapObjectType: type,
    objectKey: key,
    description,
    controllingArea: null,
    companyCode,
    costCenter: null,
    responsible: null,
    status: null,
    attributes,
    chargeObjectTypeId: null,
    active: true,
    importedAt: '',
    source: 'sap_import',
  }
}

/**
 * Build charge codes from the charge objects that a week's TimeSheet ROWS
 * actually reference. The WBS-structure catalog (loadCatsChargeObjectCodes) is
 * built from a different service and often does not carry — or carries in a
 * different WBS representation — the exact charge objects a CATS record posted
 * against. Registering the entry's OWN charge object (same synthetic id that
 * `chargeCodeIdForRow` produces, so it resolves) guarantees every entry renders
 * as a grid line and shows up in the picker, instead of being counted only in
 * the daily total ("disappearing"). Deduped by id; label = the readable
 * WBS / order / cost center the record carries.
 */
export function chargeObjectsFromRows(rows: readonly TimeSheetRow[]): TsChargeCode[] {
  const byId = new Map<string, SapChargeObject>()
  for (const row of rows) {
    const id = chargeCodeIdForRow(row)
    if (!id || byId.has(id)) continue
    const { type, key, operation } = splitChargeCodeId(id)
    const label = (row.WbsElement || row.InternalOrder || row.SenderCostCenter || key).trim()
    const object = toSapChargeObject(type ?? 'wbs_element', key, label, row.CompanyCode ?? null, row as unknown as Record<string, unknown>)
    // An order + operation line keeps the order as its SAP key but is its own
    // grid line, so it carries the composite id and names the operation.
    byId.set(id, operation ? { ...object, id, description: `${key} · Op ${operation}` } : object)
  }
  return [...byId.values()].map(sapChargeObjectToChargeCode)
}

/**
 * Load synced SAP charge objects (WBS + production orders) as timesheet charge
 * codes. Both sets are identical here (everything read is active); `all` doubles
 * as the resolver. Best-effort per source — one service being down still returns
 * the other's objects.
 */
export async function loadCatsChargeObjectCodes(): Promise<ChargeObjectCodeSets> {
  const [wbs, prd] = await Promise.all([
    odataListAt<WbsRow>(WBS_URL, { select: ['WbsElement', 'WbsElementExternal', 'Description', 'CompanyCode'], top: PAGE_TOP })
      .catch((err) => { console.warn('[timesheet-cats] WBS load failed', err); return [] as WbsRow[] }),
    odataListAt<PrdOrdRow>(PRDORD_URL, { select: ['ProductionOrder', 'ProductionOrderExternal', 'Description'], top: PAGE_TOP })
      .catch((err) => { console.warn('[timesheet-cats] production-order load failed', err); return [] as PrdOrdRow[] }),
  ])
  const objects: SapChargeObject[] = [
    ...wbs.map(wbsRowToChargeObject),
    ...prd.map(prdOrdRowToChargeObject),
  ].filter((o): o is SapChargeObject => o !== null)
  const codes = objects.map(sapChargeObjectToChargeCode)
  return { active: codes, all: codes }
}

interface PtoObjectRow extends WbsRow {
  PersonnelNumber?: string
  ProjectName?: string
  ResponsibleCostCenter?: string
  ProjectType?: string
}

/**
 * The employee's PTO objects (`PtoObject` on the timesheet service,
 * ZC_M12_TS_PTOOBJ): WBS elements of the configured PTO project type whose
 * responsible cost center is the employee's home cost center. Same charge-code
 * id as the WBS catalog builds, so an entry posted to one resolves as the other.
 * Labeled "PTO · <WBS description>" so the picker reads as leave.
 */
export async function loadPtoObjectCodes(pernr: string | null | undefined): Promise<TsChargeCode[]> {
  const p = normalizePernr(pernr)
  if (!p) return []
  const rows = await odataList<PtoObjectRow>('PtoObject', {
    filter: `PersonnelNumber eq '${p}'`,
    select: ['PersonnelNumber', 'WbsElement', 'WbsElementExternal', 'Description', 'ProjectName', 'ProjectType', 'ResponsibleCostCenter', 'CompanyCode'],
  })
  const codes: TsChargeCode[] = []
  for (const r of rows) {
    const object = wbsRowToChargeObject(r)
    if (!object) continue
    codes.push({
      ...sapChargeObjectToChargeCode(object),
      description: `PTO · ${(r.Description || object.objectKey).trim()}`,
      costCenter: r.ResponsibleCostCenter ?? null,
    })
  }
  return codes
}

/**
 * Load personnel from `ZUI_M12_PERSONNEL_O4` and MERGE onto the seed roster: the
 * rich seed personas (payClass / clearance / labor cats — which raw SAP HR does
 * not carry) are preserved, and any PERNR not already represented is added as a
 * minimal employee so a real S/4 user resolves. Returns the merged roster;
 * returns the seeds unchanged on any failure.
 */
export async function loadCatsEmployees(seed: readonly TsEmployee[]): Promise<TsEmployee[]> {
  const baseSelect = ['PersonnelNumber', 'FullName', 'FirstName', 'LastName', 'HomeCostCenter', 'HomeCostCenterName', 'CompanyCode', 'EmploymentStatus', 'DefaultActivityType', 'DefaultActivityTypeName', 'TimesheetCostCenter', 'TimesheetCostCenterName']
  let rows: PersonnelRow[] = []
  try {
    // Employee group / subgroup / personnel area / subarea scope pay class and
    // Time Evaluation rules. Codan's ZC_M12_PERSONNEL exposes all four; a system
    // whose view predates them rejects the $select, so retry without rather than
    // lose the whole roster.
    // WorkCategory is newer still, so it gets its own step down first.
    const siteSelect = [...baseSelect, 'EmployeeGroup', 'EmployeeSubgroup', 'PersonnelArea', 'PersonnelSubarea']
    try {
      rows = await odataListAt<PersonnelRow>(PERSONNEL_URL, { select: [...siteSelect, 'WorkCategory'], top: PAGE_TOP })
    } catch {
      try {
        rows = await odataListAt<PersonnelRow>(PERSONNEL_URL, { select: siteSelect, top: PAGE_TOP })
      } catch {
        rows = await odataListAt<PersonnelRow>(PERSONNEL_URL, { select: baseSelect, top: PAGE_TOP })
      }
    }
  } catch (err) {
    console.warn('[timesheet-cats] personnel load failed, keeping seed roster', err)
    return [...seed]
  }
  const byPernr = new Map<string, PersonnelRow>()
  for (const r of rows) {
    const pernr = (r.PersonnelNumber ?? '').trim()
    if (pernr) byPernr.set(pernr, r)
  }
  // Patch the SAP org/discipline defaults (cost center + activity type) onto the
  // seed personas too, so a seeded PERNR still gets its Organization / Discipline
  // dimensions — the seed roster does not carry these SAP fields.
  const merged: TsEmployee[] = seed.map((e) => {
    const r = (e.pernr && byPernr.get(e.pernr)) || byPernr.get(e.id)
    if (!r) return e
    const tsCc = (r.TimesheetCostCenter || '').trim()
    return {
      ...e,
      sapCostCenter: e.sapCostCenter ?? ((tsCc || r.HomeCostCenter || '').trim() || null),
      sapCostCenterName: e.sapCostCenterName ?? (((tsCc ? r.TimesheetCostCenterName : r.HomeCostCenterName) || '').trim() || null),
      sapActivityType: e.sapActivityType ?? ((r.DefaultActivityType || '').trim() || null),
      sapActivityTypeText: e.sapActivityTypeText ?? ((r.DefaultActivityTypeName || '').trim() || null),
      firstName: e.firstName ?? ((r.FirstName || '').trim() || null),
      lastName: e.lastName ?? ((r.LastName || '').trim() || null),
      sapCompanyCode: e.sapCompanyCode ?? ((r.CompanyCode || '').trim() || null),
      sapEmployeeGroup: e.sapEmployeeGroup ?? ((r.EmployeeGroup || '').trim() || null),
      sapEmployeeSubgroup: e.sapEmployeeSubgroup ?? ((r.EmployeeSubgroup || '').trim() || null),
      sapPersonnelArea: e.sapPersonnelArea ?? ((r.PersonnelArea || '').trim() || null),
      sapPersonnelSubarea: e.sapPersonnelSubarea ?? ((r.PersonnelSubarea || '').trim() || null),
      // SAP decides the work category when it reports one; the seed value is
      // only a fallback for a view that does not carry the field.
      workCategory: rowWorkCategory(r) ?? e.workCategory,
    }
  })
  const known = new Set<string>()
  for (const e of seed) {
    known.add(e.id)
    if (e.pernr) known.add(e.pernr)
  }
  for (const r of rows) {
    const pernr = (r.PersonnelNumber ?? '').trim()
    if (!pernr || known.has(pernr)) continue
    merged.push(personnelRowToEmployee(r))
    known.add(pernr)
  }
  // Pay class and the other app-level settings SAP HR does not carry, from the
  // config store: group / subgroup rules first, then per-employee settings,
  // which win. Best effort: an unreachable store leaves the roster as SAP
  // describes it.
  const { listEmployeeOverrides, applyEmployeeOverrides, listPayClassRules, applyPayClassRules } =
    await import('./catsEmployeeOverrides')
  const [rules, overrides] = await Promise.all([listPayClassRules(), listEmployeeOverrides()])
  return applyEmployeeOverrides(applyPayClassRules(merged, rules), overrides)
}

function personnelRowToEmployee(r: PersonnelRow): TsEmployee {
  const name = (r.FullName || `${r.FirstName ?? ''} ${r.LastName ?? ''}`).trim() || r.PersonnelNumber
  return {
    id: r.PersonnelNumber,
    pernr: r.PersonnelNumber,
    name,
    initials: deriveInitials(name),
    role: 'Employee',
    clearance: 'None',
    exempt: true,
    authorizedLaborCats: [],
    supervisorId: null,
    // Organization = IT0315 timesheet cost center when maintained, else IT0001
    // home cost center. Discipline = IT0315 master activity type.
    sapCostCenter: (r.TimesheetCostCenter || r.HomeCostCenter || '').trim() || null,
    sapCostCenterName: (((r.TimesheetCostCenter || '').trim() ? r.TimesheetCostCenterName : r.HomeCostCenterName) || '').trim() || null,
    sapActivityType: (r.DefaultActivityType || '').trim() || null,
    sapActivityTypeText: (r.DefaultActivityTypeName || '').trim() || null,
    firstName: (r.FirstName || '').trim() || null,
    lastName: (r.LastName || '').trim() || null,
    sapCompanyCode: (r.CompanyCode || '').trim() || null,
    sapEmployeeGroup: (r.EmployeeGroup || '').trim() || null,
    sapEmployeeSubgroup: (r.EmployeeSubgroup || '').trim() || null,
    sapPersonnelArea: (r.PersonnelArea || '').trim() || null,
    sapPersonnelSubarea: (r.PersonnelSubarea || '').trim() || null,
    workCategory: rowWorkCategory(r),
  }
}

function deriveInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '??'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
